import { z } from 'zod'
import { streamingProtocolSchema } from './common/streamingProtocol'
import {
  baseFilterBySchema,
  createFilterableListQuerySchema,
  ORDER_DIRECTIONS,
  paginationSchema,
  timestampsSchema,
} from './api'
import { encodingPresetIdSchema } from './encodingPreset'

export const LIVE_OUTPUT_TYPES = {
  srt: 'srt',
  rtmp: 'rtmp',
  whip: 'whip',
} as const
export const liveOutputTypeSchema = z.nativeEnum(LIVE_OUTPUT_TYPES)
export type LiveOutputType = z.infer<typeof liveOutputTypeSchema>

export const liveOutputIdSchema = z.string()
export type LiveOutputId = z.infer<typeof liveOutputIdSchema>

export const liveOutputDetailsSchema = z
  .object({
    liveOutputId: liveOutputIdSchema,
    name: z.string().min(1, 'Name cannot be empty'),
    protocol: streamingProtocolSchema,
  })
  .and(timestampsSchema)
export type LiveOutputDetails = z.infer<typeof liveOutputDetailsSchema>

export const liveOutputResponseSchema = z
  .object({
    items: z.array(liveOutputDetailsSchema),
  })
  .and(paginationSchema)
export type LiveOutputResponse = z.infer<typeof liveOutputResponseSchema>

export const DEFAULT_LIST_ORDER = {
  field: 'name',
  direction: ORDER_DIRECTIONS.ascending,
}

export const LIVE_OUTPUT_ORDER_BY_FIELDS = ['name', 'createdAt'] as const

export const listLiveOutputsRequestSchema = createFilterableListQuerySchema(
  [...LIVE_OUTPUT_ORDER_BY_FIELDS],
  baseFilterBySchema,
  {
    orderBy: [DEFAULT_LIST_ORDER],
  }
)
export type ListLiveOutputsRequest = z.infer<
  typeof listLiveOutputsRequestSchema
>

export const createLiveOutputSchema = z.object({
  name: z.string().min(1, 'Name cannot be empty'),
  protocol: streamingProtocolSchema,
  encodingPresetId: encodingPresetIdSchema
    .nullish()
    .transform((v) => v || null),
})
export type CreateLiveOutput = z.infer<typeof createLiveOutputSchema>

export const createLiveOutputResponseSchema = z.object({
  id: liveOutputIdSchema,
})
export type CreateLiveOutputResponse = z.infer<
  typeof createLiveOutputResponseSchema
>

export const updateLiveOutputSchema = z.object({
  name: z.string().min(1, 'Name cannot be empty').optional(),
  protocol: streamingProtocolSchema.optional(),
  encodingPresetId: encodingPresetIdSchema
    .nullish()
    .transform((v) => v || null),
})
export type UpdateLiveOutput = z.infer<typeof updateLiveOutputSchema>

export type WithLiveOutputId<T = void> = {
  liveOutputId: LiveOutputId
} & T
